import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useI18n } from '../../i18n';
import { useAnnounce, VisuallyHidden } from '../../ui/a11y';
import { Pill, StatTile } from '../../ui/DataBits';
import { TeacherFrame } from './TeacherFrame';
import { errorText, rpc, type AppError, type TeacherClass } from './api';
import { forecastPill, useTeacherHome } from './hooks';
import { EventList, type CalEvent } from '../head/R3Panels';
import { ReadinessBanner } from './ReadinessBanner';

// I1: the teacher's classes — homeroom and subject — with what's open, the
// participation forecast and the next step for each.
export function TeacherHome() {
  const { t } = useI18n();
  return (
    <TeacherFrame title={t['teacher.title']} active="classes">
      {() => <TeacherHomeBody />}
    </TeacherFrame>
  );
}

function TeacherHomeBody() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const announce = useAnnounce();
  const { data, error, reload } = useTeacherHome();
  const [startErr, setStartErr] = useState<AppError | null>(null);

  if (error) return <p className="notice notice-block" role="alert">{errorText(t, error)}</p>;
  if (!data) return <p role="status">{t.loading}</p>;

  const timeFmt = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
  const openMine = data.classes.filter((c) => c.open_session?.mine);
  const due = data.schedules.filter((s) => s.due_now);
  const classById = new Map(data.classes.map((c) => [c.id, c]));

  async function startFromSchedule(scheduleId: string, classId: string, minutes: number, tag: string | null) {
    setStartErr(null);
    const r = await rpc<{ session_id: string; code: string }>('teacher_start_session', {
      p_class_id: classId,
      p_minutes: minutes,
      p_context_tag: tag,
      p_schedule_id: scheduleId,
    });
    if (r.error) {
      setStartErr(r.error);
      return;
    }
    announce(t['session.opened'](classById.get(classId)?.label ?? '', r.data.code.split('').join(' ')));
    navigate(`/teacher/session/${r.data.session_id}`);
  }

  return (
    <>
      <ReadinessBanner state={data.school.state} />
      <div className="stat-row">
        <StatTile label={t['stat.classes']} value={data.classes.length} sub={t['ts.classesSub']} />
        <StatTile label={t['stat.open']} value={openMine.length} sub={t['ts.openSub']} />
      </div>

      {due.length > 0 && (
        <section className="panel p5" aria-labelledby="due-title">
          <h2 className="panel-title" id="due-title">{t['ts.dueTitle']}</h2>
          <ul className="due-list">
            {due.map((s) => {
              const c = classById.get(s.class_id);
              if (!c) return null;
              return (
                <li key={s.id}>
                  <span>
                    <strong>{c.label}</strong> · {t[`ts.day${s.weekday}` as 'ts.day1']} {s.start_time} · {t['ts.minutes'](s.minutes)}
                  </span>
                  {c.open_session ? (
                    <Pill kind="neutral">{t['class.open']}</Pill>
                  ) : (
                    <button type="button" className="btn btn-primary" disabled={data.school.state !== 'live'} onClick={() => startFromSchedule(s.id, c.id, s.minutes, s.context_tag)}>
                      {t['btn.startCheckin']}
                      <VisuallyHidden> – {c.label}</VisuallyHidden>
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          {startErr && <p className="error-text" role="alert">{errorText(t, startErr)}</p>}
        </section>
      )}

      {data.classes.length === 0 ? (
        <div className="panel empty">{t['ts.noClasses']}</div>
      ) : (
        <div className="class-grid">
          {data.classes.map((c) => (
            <ClassCard key={c.id} c={c} timeFmt={timeFmt} />
          ))}
        </div>
      )}

      {data.school.teacher_self_service && <AddClass schoolId={data.school.id} onAdded={reload} />}
      <p className="form-hint">{t['ts.whoSees']}</p>
      <UpcomingAtSchool />
    </>
  );
}

function ClassCard({ c, timeFmt }: { c: TeacherClass; timeFmt: Intl.DateTimeFormat }) {
  const { t } = useI18n();
  const hid = `class-${c.id}`;
  const open = c.open_session;
  return (
    <section className="panel p5 class-card" aria-labelledby={hid}>
      <div className="class-card-head">
        <h2 id={hid}>{c.label}</h2>
        {open ? <Pill kind="good">{t['class.open']}</Pill> : <Pill kind="neutral">{t['r4.noOpen']}</Pill>}
      </div>
      <dl className="kv kv-inline">
        <div>
          <dt>{t['ts.kind']}</dt>
          <dd>{c.kind === 'homeroom' ? t['ts.homeroom'] : t['ts.subject']}</dd>
        </div>
        {c.expected_size !== null && (
          <div>
            <dt>{t['ts.size']}</dt>
            <dd>{t['ts.students'](c.expected_size)}</dd>
          </div>
        )}
      </dl>
      <p className="forecast">
        <Pill kind={forecastPill(c.forecast)}>{t[`ts.fc.${c.forecast}` as 'ts.fc.likely']}</Pill>{' '}
        <span className="text-muted">{t[`ts.fcHint.${c.forecast}` as 'ts.fcHint.likely']}</span>
      </p>
      {open && open.mine && (
        <div className="session-box">
          <p className="session-meta">{t['ts.openUntil'](open.code, timeFmt.format(new Date(open.expires_at)))}</p>
        </div>
      )}
      {open && !open.mine && <p className="notice notice-block">{t['ts.colleagueOpen']}</p>}
      <div className="class-actions">
        {open?.mine ? (
          <Link className="btn btn-primary" to={`/teacher/session/${open.id}`}>
            {t['ts.openProjector']}
            <VisuallyHidden> – {c.label}</VisuallyHidden>
          </Link>
        ) : (
          <Link className="btn btn-primary" to={`/teacher/class/${c.id}`}>
            {t['ts.plan']}
            <VisuallyHidden> – {c.label}</VisuallyHidden>
          </Link>
        )}
        {c.last_session && (
          <Link className="btn btn-ghost" to={`/teacher/session/${c.last_session.id}`}>
            {t['ts.lastResults']}
            <VisuallyHidden> – {c.label}</VisuallyHidden>
          </Link>
        )}
        {open?.mine && (
          <Link className="btn btn-ghost" to={`/teacher/class/${c.id}`}>
            {t['ts.classPage']}
            <VisuallyHidden> – {c.label}</VisuallyHidden>
          </Link>
        )}
      </div>
    </section>
  );
}

// Only when the school allows teacher self-service (policy, off by default).
function AddClass({ schoolId, onAdded }: { schoolId: string; onAdded: () => void }) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  async function add(e: FormEvent) {
    e.preventDefault();
    const { data: u } = await supabase.auth.getUser();
    if (!u.user || !label.trim()) return;
    setBusy(true);
    setErr(false);
    const { error } = await supabase.from('classes').insert({ teacher_id: u.user.id, school_id: schoolId, label: label.trim() });
    setBusy(false);
    if (error) {
      setErr(true);
      return;
    }
    announce(t['addClass.added'](label.trim()));
    setLabel('');
    onAdded();
  }

  return (
    <section className="panel p5" aria-labelledby="add-class-title">
      <h2 className="panel-title" id="add-class-title">{t['addClass.title']}</h2>
      <form className="form" onSubmit={add}>
        <div className="form-row">
          <div className="field-group">
            <label className="field-label" htmlFor="new-class">{t['addClass.label']}</label>
            <input
              id="new-class"
              className="field"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t['addClass.placeholder']}
              required
              aria-invalid={err ? true : undefined}
              aria-describedby={err ? 'add-class-error' : undefined}
            />
          </div>
          <button type="submit" className="btn btn-primary btn-md" disabled={busy}>
            {busy ? t['addClass.adding'] : t['addClass.button']}
          </button>
        </div>
        {err && <p className="error-text" id="add-class-error" role="alert">{t['addClass.error']}</p>}
      </form>
    </section>
  );
}

// J9: what's coming at school in the next four weeks (exam weeks, holidays),
// to plan check-ins around. Hidden when the calendar is switched off.
function UpcomingAtSchool() {
  const { t, locale } = useI18n();
  const [ev, setEv] = useState<CalEvent[] | null>(null);
  useEffect(() => {
    const today = new Date();
    const to = new Date(today.getTime() + 28 * 864e5);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    rpc<{ events: CalEvent[] }>('school_calendar', { p_from: iso(today), p_to: iso(to) }).then((r) => setEv(r.error ? null : r.data.events));
  }, []);
  if (!ev) return null;
  return (
    <section className="panel p5" aria-labelledby="up-t">
      <h2 className="panel-title" id="up-t">{t['r3.cal.upcoming']}</h2>
      <EventList events={ev} locale={locale} />
    </section>
  );
}
